'use client';

import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { motion, AnimatePresence } from 'framer-motion';
import { LogoInfo } from '../logoInfo';
import { CiFileOn } from 'react-icons/ci';
import CustomSelect from '../common/select';

const forms = {
  'Contact Us': {
    bg: '#2B332A',
    textColor: '#B6C380',
  },
  'Work With Us': {
    bg: '#B6C380',
    textColor: '#23201F',
  },
} as const;
const projectOptions = [
  { value: '', label: 'Project type' },
  { value: 'Renovation', label: 'Renovation' },
  { value: 'Extension', label: 'Extension' },
  { value: 'Unsure', label: 'Unsure' },
];
const intendedBudgetOptions = [
  { value: '', label: 'Intended budget' },
  { value: '$250k - $500k', label: '$250k - $500k' },
  { value: '$500k - $1M', label: '$500k - $1M' },
  { value: '$1M - $1.5M', label: '$1M - $1.5M' },
  { value: '$1.5M - $2M', label: '$1.5M - $2M' },
  { value: '$1.5M - $2M', label: '$1.5M - $2M' },
  { value: '$1.5M - $2M', label: '$1.5M - $2M' },
  { value: '$2M - $3M', label: '$2M - $3M' },
  { value: '$3M+', label: '$3M+' },
];
const workingWithArchitectOptions = [
  { value: '', label: 'Are you working with an architect or designer?' },
  {
    value: "Not yet - we'd love guidance on next steps",
    label: "Not yet - we'd love guidance on next steps",
  },
  {
    value: "Yes, we're currently working with a designer (architect &/or interior designer)",
    label: "Yes, we're currently working with a designer (architect &/or interior designer)",
  },
  { value: 'Yes, we already have completed plans', label: 'Yes, we already have completed plans' },
];
const referral_source = [
  { value: '', label: 'How did you hear about us?' },
  { value: 'Google', label: 'Google' },
  { value: 'Social Media', label: 'Social Media' },
  { value: 'Friend or Referral', label: 'Friend or Referral' },
  { value: 'Other', label: 'Other' },
];
export interface inputTypeInterface {
  first_name: string;
  last_name: string;
  phone_number: string;
  email_address: string;
  message: string;
  form_type: string;
  project_type: string;
  project_location: string;
  intended_budget: string;
  working_with_an_architect_or_designer: string;
  how_did_you_hear_about_us: string;
  about_you_project: string;
  file: null | File;
}
export const defaultValue = {
  first_name: '',
  last_name: '',
  phone_number: '',
  email_address: '',
  message: '',
  form_type: '',
  project_type: '',
  project_location: '',
  intended_budget: '',
  working_with_an_architect_or_designer: '',
  how_did_you_hear_about_us: '',
  about_you_project: '',
  file: '' as string, // or: file: new DataTransfer().files as FileList
};

type FormKey = keyof typeof forms;
interface FormDataValues {
  [key: string]: string | FileList | Blob;
}

const ComponentSectionsContactTabs = ({ data }: { data: any }) => {
  const [activeForm, setActiveForm] = useState<FormKey>('Contact Us');
  const [showForm, setShowForm] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [inputs, setInputs] = useState<FormDataValues>({});

  const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>, label: FormKey): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveForm(label);
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value, files } = e.target;
    setInputs((values) => ({
      ...values,
      [name]: files ? files : value,
    }));
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const formFields: Record<FormKey, string[]> = {
    'Contact Us': [
      'first_name',
      'email_address',
      'phone_number',
      'project_type',
      'project_location',
      'intended_budget',
      'working_with_an_architect_or_designer',
      'how_did_you_hear_about_us',
      'about_you_project',
      'file',
    ],
    'Work With Us': ['first_name', 'last_name', 'phone_number', 'email_address', 'about_you_project'],
  };

  const isFileList = (val: any): val is FileList =>
    typeof window !== 'undefined' && val instanceof FileList;

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (isFileList(inputs.file) && inputs.file.length > 0) {
      const size = inputs.file[0].size;
      if (size > 10 * 1024 * 1024) {
        setStatusMessage({ type: 'error', text: 'File size must be less than 10MB.' });

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setInputs((prev) => ({ ...prev, file: '' }));
        return;
      }
    }

    setIsLoading(true);
    setStatusMessage(null);

    const payload = new FormData();
    const fieldsToSend = formFields[activeForm];
    fieldsToSend.forEach((key) => {
      if (key === 'file') return; // handle file separately
      const value = inputs[key];
      if (value !== undefined && value !== null) {
        payload.append(`data[${key}]`, value as string);
      }
    });
    payload.append('data[form_type]', activeForm);

    if (isFileList(inputs.file) && inputs.file.length > 0) {
      const imageFormData = new FormData();
      imageFormData.append('files', inputs.file[0]);

      const uploadRes = await fetch('/api/upload', {
        method: 'POST',
        body: imageFormData,
      });

      const uploadedImage = await uploadRes.json();
      if (!uploadRes.ok) {
        console.error('Upload failed', uploadedImage);
        return;
      }

      const uploadedFile = uploadedImage[0];
      payload.append('data[file]', uploadedFile.id);
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        body: payload,
      });
      if (!response.ok) {
        throw new Error('Failed to submit form');
      } else {
        setStatusMessage({
          type: 'success',
          text: 'Thank you! Your message was sent successfully.',
        });
        // Reset file input and all fields
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setInputs({ ...defaultValue });
      }
    } catch (error) {
      console.error(error);
      setStatusMessage({ type: 'error', text: 'Something went wrong. Please try again later.' });
      console.log('ComponentSectionsContactTabs data:', data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setInputs({ ...defaultValue });
    const header = document.querySelector('header');
    if (!header) return;

    if (activeForm === 'Contact Us') {
      console.log('Contact Us');
      header.classList.add('header-contactUs');
    } else if (activeForm === 'Work With Us') {
      header.classList.add('header-WorkUs');
    }

    let timer: NodeJS.Timeout;
    if (statusMessage) {
      timer = setTimeout(() => setStatusMessage(null), 7000);
    }

    // Cleanup function
    return () => {
      if (header) {
        header.classList.remove('header-contactUs', 'header-WorkUs');
      }
      if (timer) clearTimeout(timer);
    };
  }, [activeForm, statusMessage]);

  return (
    <>
      <AnimatePresence mode="wait">
        {activeForm && forms[activeForm] && (
          <motion.section
            key="form-section"
            className="min-h-screen flex flex-row md:flex-row md:items-center text-black contact-form-logininfo pb-32 xl:pb-0"
            style={{
              backgroundColor: forms[activeForm].bg,
              transition: 'background-color 0.8s',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mx-auto mt-16 md:mt-0 px-4 xl:max-w-[68vw] 2xl:max-w-[55vw] w-full">
              <motion.form
                className="w-full"
                onSubmit={handleSubmit}
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      staggerChildren: 0.1,
                    },
                  },
                }}
              >
                <div
                  className={`flex md:items-center gap-10 md:gap-0 flex-col-reverse md:flex-row justify-between border-b ${
                    activeForm === 'Contact Us'
                      ? 'border-[#B6C380]'
                      : activeForm === 'Work With Us'
                        ? 'border-[#23201F]'
                        : 'border-[#B6C380]'
                  } pb-4 md:pb-5`}
                >
                  <h2
                    className={`text-[20px] md:text-[22px] font-light leading-none ${
                      activeForm === 'Contact Us'
                        ? 'text-[#B6C380]'
                        : activeForm === 'Work With Us'
                          ? 'text-[#23201F]'
                          : 'text-[#B6C380]'
                    }`}
                  >
                    {activeForm}
                  </h2>
                  <div
                    className={`flex space-x-2 -mx-4 md:-mx-0 md:space-x-5 px-4 md:px-0 text-[15px] md:text-base font-light ${
                      activeForm === 'Contact Us'
                        ? 'text-[#B6C380] border-[#B6C380]'
                        : activeForm === 'Work With Us'
                          ? 'text-[#23201F] border-[#23201F]'
                          : 'text-[#B6C380]'
                    }  py-4 md:py-0 leading-none border-b border-[#23201F]/40 md:border-0`}
                  >
                    {(Object.keys(forms) as FormKey[]).map((label) => (
                      <span
                        key={label}
                        className={classNames('cursor-pointer select-none', {
                          'opacity-40': activeForm !== label,
                        })}
                        onClick={() => {
                          if (activeForm === label) {
                            setShowForm((prev) => !prev);
                            if (showForm) {
                              setActiveForm('Contact Us');
                            }
                          } else {
                            setActiveForm(label);
                            setShowForm(true);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => handleKeyDown(e, label)}
                        aria-pressed={activeForm === label}
                      >
                        {activeForm === label ? '●' : '○'} {label}
                      </span>
                    ))}
                  </div>
                </div>
                {activeForm === 'Contact Us' && showForm && (
                  <div className="contact-form grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-0">
                    {/* Full Name */}
                    <motion.div
                      key="full-name"
                      className="relative"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="first_name" className="sr-only">
                        Full name
                      </label>
                      <input
                        type="text"
                        required
                        id="first_name"
                        placeholder="Full name"
                        className="w-full border-b border-[#B6C38066] leading-16 bg-transparent text-[14px] font-light text-[#B6C380] placeholder:text-[#B6C38066] py-[10px] focus:outline-none"
                        aria-label="Full name"
                        value={typeof inputs.first_name === 'string' ? inputs.first_name : ''}
                        name="first_name"
                        onChange={handleInputChange}
                      />
                      <span className="requird-text font-light">Required</span>
                    </motion.div>
                    {/* Email Address */}
                    <motion.div
                      className="relative"
                      key="email"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="email_address" className="sr-only">
                        Email address
                      </label>
                      <input
                        type="email"
                        required
                        id="email_address"
                        placeholder="Email address"
                        className="w-full border-b border-[#B6C38066] leading-16 bg-transparent text-[14px] font-light text-[#B6C380] placeholder:text-[#B6C38066] py-[10px] focus:outline-none"
                        aria-label="Email address"
                        value={typeof inputs.email_address === 'string' ? inputs.email_address : ''}
                        name="email_address"
                        onChange={handleInputChange}
                      />
                      <span className="requird-text font-light">Required</span>
                    </motion.div>
                    {/* Phone Number */}
                    <motion.div
                      key="phone-number"
                      className="relative"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="phone_number" className="sr-only">
                        Phone number
                      </label>
                      <input
                        type="text"
                        id="phone_number"
                        required
                        placeholder="Phone number"
                        className="w-full border-b border-[#B6C38066] leading-16 bg-transparent text-[14px] font-light text-[#B6C380] placeholder:text-[#B6C38066] py-[10px] focus:outline-none"
                        aria-label="Phone number"
                        value={typeof inputs.phone_number === 'string' ? inputs.phone_number : ''}
                        name="phone_number"
                        onChange={handleInputChange}
                      />
                      <span className="requird-text font-light">Required</span>
                    </motion.div>
                    {/* Project Type */}
                    <motion.div
                      key="project-type"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="project_type" className="sr-only">
                        Project type
                      </label>
                      <CustomSelect
                        name="project_type"
                        options={projectOptions}
                        value={typeof inputs.project_type === 'string' ? inputs.project_type : ''}
                        onChange={handleInputChange}
                        placeholder="Project type"
                      />
                    </motion.div>
                    {/* Project location */}
                    <motion.div
                      key="project-location"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="project-location" className="sr-only">
                        Project location
                      </label>
                      <input
                        type="text"
                        id="project-location"
                        placeholder="Project location"
                        className="w-full border-b border-[#B6C38066] leading-16 bg-transparent text-[14px] font-light text-[#B6C380] placeholder:text-[#B6C38066] py-[10px] focus:outline-none"
                        aria-label="Project location"
                        value={
                          typeof inputs.project_location === 'string' ? inputs.project_location : ''
                        }
                        name="project_location"
                        onChange={handleInputChange}
                      />
                    </motion.div>
                    {/* Intended Budget */}
                    <motion.div
                      key="budget"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="budget" className="sr-only">
                        Intended budget
                      </label>
                      <CustomSelect
                        name="intended_budget"
                        options={intendedBudgetOptions}
                        value={
                          typeof inputs.intended_budget === 'string' ? inputs.intended_budget : ''
                        }
                        onChange={handleInputChange}
                        placeholder="Intended budget"
                      />
                    </motion.div>
                    {/* Working with Architect */}
                    <motion.div
                      key="architect"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="working_with_architect" className="sr-only">
                        Are you working with an architect or designer?
                      </label>
                      <CustomSelect
                        name="working_with_an_architect_or_designer"
                        options={workingWithArchitectOptions}
                        value={
                          typeof inputs.working_with_an_architect_or_designer === 'string'
                            ? inputs.working_with_an_architect_or_designer
                            : ''
                        }
                        onChange={handleInputChange}
                        placeholder="Are you working with an architect or designer?"
                      />
                    </motion.div>
                    {/* How did you hear about us? */}
                    <motion.div
                      key="referral"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="referral_source" className="sr-only">
                        How did you hear about us?
                      </label>
                      <CustomSelect
                        name="how_did_you_hear_about_us"
                        options={referral_source}
                        value={
                          typeof inputs.how_did_you_hear_about_us === 'string'
                            ? inputs.how_did_you_hear_about_us
                            : ''
                        }
                        onChange={handleInputChange}
                        placeholder="Are you working with an architect or designer?"
                      />
                    </motion.div>
                    {/* Message */}
                    <motion.div
                      key="message"
                      className="md:col-span-2"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="message" className="sr-only">
                        Tell us about your project
                      </label>
                      <textarea
                        id="message"
                        placeholder="Tell us a little about your project & what you are hoping to achieve"
                        className="w-full border-b border-[#B6C38066] bg-transparent text-[14px] font-light text-[#B6C380] placeholder:text-[#B6C38066] py-[16px] focus:outline-none"
                        aria-label="Project message"
                        value={
                          typeof inputs.about_you_project === 'string'
                            ? inputs.about_you_project
                            : ''
                        }
                        name="about_you_project"
                        onChange={handleInputChange}
                        rows={4}
                      ></textarea>
                    </motion.div>
                    {/* File Upload */}
                    <motion.div
                      key="file"
                      className="md:col-span-2"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label
                        htmlFor="file"
                        className="sr-only ring-transparent border-0 outline-none focus:outline-none"
                      >
                        {' '}
                        Upload File{' '}
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.png"
                        name="file"
                        className="hidden"
                        aria-label="Upload File"
                        onChange={handleInputChange}
                        ref={fileInputRef}
                      />
                      <button
                        type="button"
                        onClick={handleButtonClick}
                        className="py-3 leading-none px-0 flex w-full text-[14px] items-center justify-between focus:outline-none gap-2 text-[#B6C38066] font-light"
                      >
                        {' '}
                        Upload File
                        {isFileList(inputs.file) && inputs.file.length > 0 && (
                          <p className="text-sm text-[#B6C380] text-center truncate">
                            Selected: {inputs.file[0].name}
                          </p>
                        )}
                        <CiFileOn size={20} />
                      </button>
                    </motion.div>
                    {/* File Upload */}
                    {/* <motion.div
                      key="file"
                      className="md:col-span-2"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label
                        htmlFor="file"
                        className="sr-only ring-transparent border-0 outline-none focus:outline-none"
                      >
                        Upload File
                      </label>
                      <input type="file" accept=".pdf,.doc,.docx,.jpg,.png" value={inputs.file || ''} name="file" onChange={handleInputChange} />
                    </motion.div>         */}
                    <motion.button
                      type="submit"
                      className="md:col-span-2 py-3 font-light text-[14px] tracking-wide w-full bg-[#B6C380] hover:text-[#2B332A] hover:bg-opacity-90 transition-all text-[#2B332A] leading-none"
                      // whileTap={{ scale: 0.95 }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Submitting...' : 'Submit'}
                    </motion.button>
                  </div>
                )}

                {activeForm === 'Work With Us' && showForm && (
                  <div className="Work-with-us">
                    {/* Message */}
                    <motion.div
                      key="message"
                      className="md:col-span-2"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="message" className="sr-only">
                        Tell us about your project
                      </label>
                      <textarea
                        id="message"
                        placeholder="We are always looking for exceptional staff. Tell us about yourself and what you are looking for"
                        className="w-full border-b border-[#B6C38066] bg-transparent text-[14px] font-light text-[#23201F] placeholder:text-[#23201F66] py-2 focus:outline-none"
                        aria-label="Project message"
                        value={
                          typeof inputs.about_you_project === 'string'
                            ? inputs.about_you_project
                            : ''
                        }
                        name="about_you_project"
                        onChange={handleInputChange}
                        rows={4}
                      ></textarea>
                    </motion.div>

                    <motion.div
                      key="first-name"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="first_name" className="sr-only">
                        First name
                      </label>
                      <input
                        type="text"
                        id="first_name"
                        placeholder="First name"
                        className="w-full border-b border-[#23201F] bg-transparent text-[14px] font-light text-[#23201F] placeholder:text-[#23201F66] py-[10px] focus:outline-none"
                        aria-label="First name"
                        value={typeof inputs.first_name === 'string' ? inputs.first_name : ''}
                        name="first_name"
                        onChange={handleInputChange}
                      />
                    </motion.div>

                    <motion.div
                      key="last-name"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="last_name" className="sr-only">
                        Last name
                      </label>
                      <input
                        type="text"
                        id="last_name"
                        placeholder="Last name"
                        className="w-full border-b border-[#23201F] bg-transparent text-[14px] font-light text-[#23201F] placeholder:text-[#23201F66] py-[10px] focus:outline-none"
                        aria-label="Last name"
                        value={typeof inputs.last_name === 'string' ? inputs.last_name : ''}
                        name="last_name"
                        onChange={handleInputChange}
                      />
                    </motion.div>

                    <motion.div
                      key="phone-number"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="phone_number" className="sr-only">
                        Phone number
                      </label>
                      <input
                        type="text"
                        id="phone_number"
                        placeholder="Phone number"
                        className="w-full border-b border-[#23201F] bg-transparent text-[14px] font-light text-[#23201F] placeholder:text-[#23201F66] py-[10px] focus:outline-none"
                        aria-label="Phone number"
                        value={typeof inputs.phone_number === 'string' ? inputs.phone_number : ''}
                        name="phone_number"
                        onChange={handleInputChange}
                      />
                    </motion.div>

                    <motion.div
                      key="email-address"
                      variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
                    >
                      <label htmlFor="email_address" className="sr-only">
                        Email address
                      </label>
                      <input
                        type="email"
                        id="email_address"
                        placeholder="Email address"
                        className="w-full border-b border-[#23201F] bg-transparent text-[14px] font-light text-[#23201F] placeholder:text-[#23201F66] py-[10px] focus:outline-none"
                        aria-label="Email address"
                        value={typeof inputs.email_address === 'string' ? inputs.email_address : ''}
                        name="email_address"
                        onChange={handleInputChange}
                      />
                    </motion.div>
                    <motion.button
                      type="submit"
                      className="py-3 font-light text-[14px] tracking-wide w-full bg-[#23201F] hover:text-[#B6C380] hover:bg-opacity-90 transition-all text-[#B6C380] leading-none"
                      // whileTap={{ scale: 0.95 }}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Submitting...' : 'Submit'}
                    </motion.button>
                  </div>
                )}
              </motion.form>

              {statusMessage && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mt-4 text-sm font-medium ${
                    statusMessage.type === 'success'
                      ? 'text-green-600 w-max py-1 px-2 leading-none'
                      : 'text-red-600 bg-[#E5E1D5] w-max py-1 px-2 leading-none'
                  }`}
                >
                  {statusMessage.text}
                </motion.p>
              )}
            </div>

            <LogoInfo
              width={50}
              height={70}
              fillColor={forms[activeForm].textColor}
              textColor={forms[activeForm].textColor}
            />
          </motion.section>
        )}
      </AnimatePresence>
    </>
  );
};

export default ComponentSectionsContactTabs;
